import Permissions from '@/app/permissions/Permissions';
import ICONS from '../../../common-utils/enums/icons';
import logoPayludd from 'url:../../../../resource/images/payludd.png';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import useCustomPayStore from '@/app/store/customPay';

/**
 * Session menu access: visible for any authenticated user with at least one permission.
 * @param {string[]} permissions
 * @returns {boolean}
 */
const hasSessionMenuAccess = (permissions) =>
    !!permissions?.length && hasRequiredPermissions({ permissions, requiredPermissions: permissions });

const isCustomPay = useCustomPayStore.getState().isCustomPay;

export default {
    HOME: {
        url: '/',
        icon: ICONS.HOME,
        entity: 'page.home.title'
    },
    ORGANIZATIONS_TREE: {
        url: '/org-tree',
        icon: ICONS.ORGANIZATIONS,
        entity: 'page.organizations.tree_tmp'
    },
    ORGANIZATIONS: {
        url: '/organizations',
        icon: ICONS.ORGANIZATION,
        entity: 'page.organizations.title',
        id: 'group1-config-organizations',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.ORGANIZATION_LIST.name] })
    },
    ORGANIZATION_DETAILS: {
        url: '/organizations/:organizationId',
        icon: ICONS.ORGANIZATION,
        entity: i18nData => i18nData.socialReason && i18nData.organizationType ? 'page.organization.title' : 'page.organization.short',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.ORGANIZATION_READ.name] })
    },
    ORGANIZATION_USER_DETAILS: {
        url: '/organizations/:organizationId/users/:userId',
        icon: ICONS.USER,
        entity: i18nData => i18nData.login ? 'user' : 'user_short'
    },
    ORGANIZATION_SUBSCRIPTION_DETAILS: {
        url: '/organizations/:organizationId/subscriptions/:subscriptionId',
        icon: ICONS.SUBSCRIPTION,
        entity: 'page.subscription.title'
    },
    MERCHANT_CONTRACTS_DETAILS: {
        url: '/subscriptions/:subscriptionId/pos-terminals/:posTerminalId/merchant-contracts/:merchantContractId',
        icon: ICONS.MERCHANT_CONTRACT,
        entity: i18nData => i18nData.number ? 'merchantContract' : 'merchantContract_short',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.MERCHANT_CONTRACT_READ.name] })
    },
    SEARCH_CONTRACTS: {
        url: '/search',
        icon: ICONS.SEARCH,
        entity: 'page.searchContracts.title',
        id: 'group1-search',
        hasPermissions: (permissions) => hasRequiredPermissions({
            permissions: permissions,
            requiredPermissions: [Permissions.MERCHANT_CONTRACT_LIST_FROM_PROVIDER.name]
        })
    },
    SUBSCRIPTIONS: {
        url: '/subscriptions',
        icon: ICONS.POS_TERMINAL,
        entity: 'page.subscriptions.title',
        id: 'group1-parc-subscriptions',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.SUBSCRIPTION_LIST.name] })
    },
    SUBSCRIPTION_DETAILS: {
        url: '/subscriptions/:subscriptionId',
        icon: ICONS.SUBSCRIPTION,
        entity: i18nData => i18nData && i18nData.serialNumber ? 'page.subscription.details.full' : 'page.subscription.details.short',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.SUBSCRIPTION_READ.name] })
    },
    POS_TERMINAL_DETAILS: {
        url: '/subscriptions/:subscriptionId/pos-terminals/:posTerminalId',
        icon: ICONS.POS_TERMINAL,
        entity: i18nData => i18nData && i18nData.serialNumber ? 'posTerminal' : 'posTerminal_short',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.POS_TERMINAL_READ.name] })
    },
    POS_TERMINAL_TRANSACTIONS: {
        url: '/subscriptions/:subscriptionId/pos-terminals/:posTerminalId/transactions',
        icon: ICONS.CREDIT_CARD,
        entity: i18nData => i18nData && i18nData.serialNumber ? 'posTerminal' : 'posTerminal_short',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.POS_TERMINAL_READ.name] })
    },
    PLANS: {
        url: '/plans',
        icon: ICONS.PLANS,
        entity: 'page.plans.title',
        id: 'group1-config-plans',
        hasPermissions: (permissions) => hasRequiredPermissions({
            permissions: permissions,
            requiredPermissions: [Permissions.SUBSCRIPTION_PLAN_LIST_CLIENT_PLANS.name]
        })
    },
    PLAN_DETAILS: {
        url: '/plans/:planId',
        icon: ICONS.PLAN,
        entity: 'page.plans.details.title',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.SUBSCRIPTION_PLAN_READ.name] })
    },
    SIM_CARDS: {
        url: '/sims',
        icon: ICONS.GPRS,
        entity: 'page.sim_cards.title',
        id: 'group1-parc-sims',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.SIM_LIST.name] })
    },
    SIM_CARDS_DETAILS: {
        url: '/sims/:simCardId',
        icon: ICONS.GPRS,
        entity: i18nData => i18nData.iccid ? 'page.sim_card.details.full' : 'page.sim_card.details.short',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.SIM_READ.name] })
    },
    SIM_CARDS_GROUP_DETAILS: {
        url: '/sims/groups/:groupId',
        icon: ICONS.GPRS,
        entity: 'page.sim_group.title'
    },
    REMOTE_COLLECTION_TRANSACTIONS: {
        url: '/remote-collections/:remoteCollectionId',
        icon: ICONS.CREDIT_CARD,
        entity: (i18nData) => i18nData.number && i18nData.application && i18nData.reconciliationNumber ?
            'page.remote_collection.transaction.title.full'
            : i18nData.number && i18nData.reconciliationNumber && !i18nData.application ?
                'page.remote_collection.transaction.title.without_application'
                : i18nData.number && !i18nData.reconciliationNumber && !i18nData.application ?
                    'page.remote_collection.transaction.title.contract_only'
                    : 'page.remote_collection.transaction.title.short',
        hasPermissions: (permissions) => hasRequiredPermissions({
            permissions: permissions,
            requiredPermissions: [Permissions.PAYMENT_LIST_REMOTE_COLLECTION_TRANSACTIONS.name, Permissions.PAYMENT_LIST_REMOTE_COLLECTION_PLBS.name],
            hasAtLeastOnePermission: true
        })
    },
    REMOTE_COLLECTION: {
        url: '/remote-collections',
        icon: ICONS.REMOTE_COLLECTION,
        entity: 'page.remote_collection.title',
        id: 'group1-exploitation-remoteCollections',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.PAYMENT_LIST_REMOTE_COLLECTIONS.name] })
    },
    USERS: {
        url: '/users',
        icon: ICONS.USERS,
        entity: 'page.users.title',
        id: 'group1-config-users',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.USER_LIST.name] })
    },
    USER_DETAILS: {
        url: '/users/:userId',
        icon: ICONS.USER,
        entity: 'page.users.details.title',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.USER_READ.name] })
    },
    MERCHANT_ACCOUNTS: {
        url: '/contractsAccesses',
        icon: ICONS.USERS,
        entity: 'page.merchant_accounts.title',
        id: 'group1-config-contractsAccesses',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.CONTRACTS_ACCESS_LIST.name] })
    },
    MERCHANT_ACCOUNT_DETAILS: {
        url: '/contractsAccesses/:contractAccessId',
        icon: ICONS.USER,
        entity: i18nData => i18nData && i18nData.label ? i18nData.label : 'page.merchant_accounts.details.title',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.CONTRACTS_ACCESS_READ.name] })
    },
    RENTAL_FILES: {
        url: '/subscriptions/:subscriptionId/pos-terminals/:posTerminalId/remote-collections/:remoteCollectionId/rentalFiles',
        icon: ICONS.FOLDER,
        entity: 'page.rentalFiles.title',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.PAYMENT_LIST_REMOTE_COLLECTION_PLBS.name] })
    },
    RENTAL_FILES_DETAILS: {
        url: '/subscriptions/:subscriptionId/pos-terminals/:posTerminalId/remote-collections/:remoteCollectionId/rentalFile/:rentalFileId',
        icon: ICONS.FOLDER,
        entity: i18nData => i18nData.fileNumber ? 'rentalFile' : 'rentalFile_short',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.PAYMENT_READ_PLBS_DETAILS.name] })
    },
    NEWSLETTER: {
        url: '/newsletter',
        icon: ICONS.NEW_RELEASES,
        entity: 'page.newsletter.title',
        id: 'group2-newsletter',
        hasPermissions: hasSessionMenuAccess
    },
    TOKEN: {
        url: '/token'
    },
    DOCUMENTATION: {
        url: '/documentation',
        icon: ICONS.DOCUMENTATION,
        entity: 'page.documentation.title',
        isBold: true,
        id: 'group2-documentation',
        hasPermissions: hasSessionMenuAccess
    },
    DOCUMENTATION_API: {
        url: '/documentation/apis',
        icon: ICONS.API,
        entity: 'page.documentation.apis'
    },
    DOCUMENTATION_USER_GUIDE: {
        url: '/documentation/guide',
        icon: ICONS.USER_GUIDE,
        entity: 'page.documentation.user_guide'
    },
    DOCUMENTATION_USER_GUIDE_BOMA: {
        url: '/documentation/merchant-guide',
        icon: ICONS.USER_GUIDE,
        entity: 'page.documentation.user_merchant_guide'
    },
    DOCUMENTATION_PAYLUDD: {
        url: '/documentation/payludd-init',
        icon: false,
        image: logoPayludd,
        entity: 'page.documentation.payludd_guide'
    },
    MERCHANT_DOCUMENTATION_PAYLUDD: {
        url: '/documentation/payludd-merchant-guide',
        icon: false,
        image: logoPayludd,
        entity: 'page.documentation.merchant_payludd_guide'
    },
    ENVELOPES: {
        url: '/envelopes',
        icon: ICONS.ENVELOPES,
        entity: 'page.envelopes.title',
        id: 'group1-exploitation-envelopes',
        hasPermissions: (permissions) => hasRequiredPermissions({
            permissions: permissions,
            requiredPermissions: [Permissions.LIST_REMOTE_DOWNLOADS.name]
        })
    },
    PUSH_PAYMENTS: {
        url: '/pushPayments',
        icon: ICONS.PUSH_PAYMENTS,
        entity: 'page.push_payments.title',
        id: 'group1-config-pushPayments',
        hasPermissions: (permissions) => !isCustomPay && hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.PUSH_PAYMENT_READ.name] })
    },
    HISTORICS: {
        url: '/historics',
        icon: ICONS.HISTORY,
        entity: 'page.historics.title',
        id: 'group1-historics',
        hasPermissions: (permissions) => hasRequiredPermissions({ permissions: permissions, requiredPermissions: [Permissions.LIST_HISTORY.name] })
    },
    LOGIN: {
        url: '/login'
    },
    PAYLUDD_CUSTOMIZATION_CREATION: {
        url: '/contractsAccesses/:contractAccessId/customization-creation',
        icon: ICONS.POS_TERMINAL,
        entity: 'page.payludd.customization.creation.title',
        previousBtn: true
    },
    PAYLUDD_CUSTOMIZATION_MANAGEMENT: {
        url: '/contractsAccesses/:contractAccessId/customization-management/:customizationId',
        icon: ICONS.POS_TERMINAL,
        entity: i18nData => i18nData && i18nData.packageLabel ? i18nData.packageLabel : 'page.payludd.customization.management.title',
        previousBtn: true
    }
};